import React, { useState, useEffect, useMemo } from 'react';
import { X, Search, ChevronLeft, ChevronRight, ArrowUpDown } from 'lucide-react';
import api from '../api/axios';
import {
  formatUiCurrencyCompact,
  formatUiDate,
  formatUiDateTime,
  formatUiHourLabel,
  formatUiTime,
} from '../utils/uiFormat';

const PAGE_SIZE = 50;

const compareValues = (left, right, direction = 'asc') => {
  const va = left ?? '';
  const vb = right ?? '';
  const na = Number(va);
  const nb = Number(vb);
  const bothNumeric = va !== '' && vb !== '' && !Number.isNaN(na) && !Number.isNaN(nb);

  if (bothNumeric) {
    return direction === 'asc' ? na - nb : nb - na;
  }

  return direction === 'asc'
    ? String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' })
    : String(vb).localeCompare(String(va), undefined, { numeric: true, sensitivity: 'base' });
};

const getSearchText = (value) => {
  if (value == null) return '';
  if (Array.isArray(value)) return value.map(getSearchText).join(' ');
  if (typeof value === 'object') return Object.values(value).map(getSearchText).join(' ');
  return String(value);
};

const isIsoDate = (value) => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
const isIsoDateTime = (value) => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value);
const isTimeOnly = (value) => typeof value === 'string' && /^\d{2}:\d{2}(:\d{2})?$/.test(value);

const DashboardDrillDown = ({ isOpen, onClose, drillType, params, title: propTitle, apiUrl, directData, directSummary, onOrderClick, onRowClick, zIndexClass = 'z-50' }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState('');
  const [sortDir, setSortDir] = useState('asc');
  const [filterDept, setFilterDept] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [activeTab, setActiveTab] = useState('detail');

  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setPage(1);
      setSortKey('');
      setFilterDept('');
      setFilterCategory('');
      setActiveTab('detail');
      if (directData) {
        setResult({ title: propTitle || 'Chart Data', data: directData, summary: directSummary || {} });
        setLoading(false);
      } else if (drillType) {
        fetchData();
      }
    }
  }, [isOpen, drillType, params, directData]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const queryParams = new URLSearchParams({ type: drillType, ...params });
      const endpoint = apiUrl || '/dashboard/drill-down/';
      const response = await api.get(`${endpoint}?${queryParams}`);
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => formatUiCurrencyCompact(amount);

  // Get unique departments and categories for filter dropdowns
  const departments = useMemo(() => {
    if (!result?.data) return [];
    return [...new Set(result.data.map(r => r.department).filter(Boolean))];
  }, [result]);

  const categories = useMemo(() => {
    if (!result?.data) return [];
    let data = result.data;
    if (filterDept) data = data.filter(r => r.department === filterDept);
    return [...new Set(data.map(r => r.category).filter(Boolean))];
  }, [result, filterDept]);

  // Filter + search + sort
  const filteredData = useMemo(() => {
    if (!result?.data) return [];
    let data = [...result.data];

    if (filterDept) data = data.filter(r => r.department === filterDept);
    if (filterCategory) data = data.filter(r => r.category === filterCategory);

    if (search) {
      const s = search.toLowerCase();
      data = data.filter(r =>
        Object.values(r).some(v => getSearchText(v).toLowerCase().includes(s))
      );
    }

    if (sortKey) {
      data.sort((a, b) => compareValues(a[sortKey], b[sortKey], sortDir));
    }

    return data;
  }, [result, search, sortKey, sortDir, filterDept, filterCategory]);

  const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);
  const pagedData = filteredData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Filtered summary
  const filteredSummary = useMemo(() => {
    const qty = filteredData.reduce((s, r) => s + (r.quantity || 0), 0);
    const amount = filteredData.reduce((s, r) => s + (r.total_price || 0), 0);
    return { qty, amount, count: filteredData.length };
  }, [filteredData]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  };


  if (!isOpen) return null;

  const columns = pagedData.length > 0 ? Object.keys(pagedData[0]).filter(k => !k.startsWith('__') && k !== 'is_previous_sale') : [];

  const columnLabels = {
    order_number: 'Order #',
    date: 'Date',
    time: 'Time',
    item_name: 'Item',
    item: 'Item',
    category: 'Category',
    department: 'Department',
    quantity: 'Qty',
    unit_price: 'Unit Price',
    total_price: 'Total',
    total: 'Total',
    cashier: 'Cashier',
    payment_method: 'Payment',
    menu_item__name: 'Item',
    total_qty: 'Qty Sold',
    total_sales: 'Sales Amount',
    dept: 'Department',
    dept_name: 'Department',
    item_count: 'Items',
    items_count: 'Items',
    wastage_number: 'Wastage #',
    type: 'Type',
    cost_impact: 'Cost Impact',
    reason: 'Reason',
    status: 'Status',
    reported_by: 'Reported By',
    recorded_by: 'Recorded By',
    expense_number: 'Expense #',
    amount: 'Amount',
    description: 'Description',
    name: 'Name',
    revenue: 'Revenue',
    avg_price: 'Avg Price',
    menu_item_id: 'Item ID',
    count: 'Orders',
    display: 'Hour',
    label: 'Time Slot',
    avg_per_order: 'Avg/Order',
    qty: 'Qty Sold',
    cost: 'Cost',
    wastage_type: 'Type',
    raw_material__name: 'Item',
    category_display: 'Category',
    menu_item__category__name: 'Category',
  };

  const renderCellValue = (col, value) => {
    if (['total_price','unit_price','cost_impact','amount','total','revenue','avg_price','cost','avg_per_order'].includes(col)) {
      return formatCurrency(value);
    }
    if (col === 'payment_method') {
      return (value || '').replace('_', ' ');
    }
    if (isIsoDateTime(value) || /(_at|date_time|punch_time)$/i.test(col)) {
      return formatUiDateTime(value);
    }
    if (isIsoDate(value) || /(^date$|_date$|effective_from$|start_date$|end_date$)/i.test(col)) {
      return formatUiDate(value);
    }
    if (col === 'display' || col === 'hour' || col === 'label') {
      return formatUiHourLabel(value);
    }
    if (isTimeOnly(value) || /(^time$|_time$|shift_start$|shift_end$)/i.test(col)) {
      return formatUiTime(value, { seconds: String(value || '').length > 5 });
    }
    return value;
  };

  const renderSummaryCards = () => {
    if (result?.summary) {
      const s = result.summary;
      const cards = [];
      if (s.total_orders !== undefined) cards.push({ label: 'Total Orders', value: s.total_orders, color: 'green' });
      if (s.total_entries !== undefined) cards.push({ label: 'Total Entries', value: s.total_entries, color: 'green' });
      if (s.unique_items !== undefined) cards.push({ label: 'Unique Items', value: s.unique_items, color: 'green' });
      if (s.total_items !== undefined) cards.push({ label: 'Items Sold', value: s.total_items, color: 'blue' });
      if (s.total_revenue !== undefined) cards.push({ label: 'Revenue', value: formatCurrency(s.total_revenue), color: 'blue' });
      if (s.total_amount !== undefined) cards.push({ label: 'Total Amount', value: formatCurrency(s.total_amount), color: 'purple' });
      if (s.total_cost !== undefined) cards.push({ label: 'Total Cost', value: formatCurrency(s.total_cost), color: 'red' });
      if (s.peak_hour !== undefined) cards.push({ label: 'Peak Hour', value: formatUiHourLabel(s.peak_hour), color: 'orange' });
      if (cards.length === 0) return null;
      const colorMap = { green: ['bg-green-50','border-green-200','text-green-600','text-green-800'], blue: ['bg-blue-50','border-blue-200','text-blue-600','text-blue-800'], purple: ['bg-purple-50','border-purple-200','text-purple-600','text-purple-800'], red: ['bg-red-50','border-red-200','text-red-600','text-red-800'], orange: ['bg-orange-50','border-orange-200','text-orange-600','text-orange-800'] };
      return (
        <div className={`grid grid-cols-${Math.min(cards.length, 4)} gap-3 mb-4`}>
          {cards.map((c, i) => {
            const cm = colorMap[c.color] || colorMap.green;
            return (
              <div key={i} className={`${cm[0]} border ${cm[1]} rounded-lg p-3 text-center`}>
                <p className={`text-xs ${cm[2]} font-medium`}>{c.label}</p>
                <p className={`text-lg font-bold ${cm[3]}`}>{c.value}</p>
              </div>
            );
          })}
        </div>
      );
    }
    // Default filtered summary
    return (
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
          <p className="text-xs text-green-600 font-medium">Records</p>
          <p className="text-lg font-bold text-green-800">{filteredSummary.count}</p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
          <p className="text-xs text-blue-600 font-medium">Total Qty</p>
          <p className="text-lg font-bold text-blue-800">{filteredSummary.qty}</p>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center">
          <p className="text-xs text-purple-600 font-medium">Total Amount</p>
          <p className="text-lg font-bold text-purple-800">{formatCurrency(filteredSummary.amount)}</p>
        </div>
      </div>
    );
  };

  const renderSummaryTab = () => {
    if (result?.item_summary) {
      const summaryRows = [...result.item_summary].sort((a, b) => {
        if (!sortKey) {
          return compareValues(b.total_sales, a.total_sales, 'asc');
        }
        return compareValues(a[sortKey], b[sortKey], sortDir);
      });

      return (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th onClick={() => handleSort('menu_item__name')} className="text-left p-3 font-semibold text-gray-600 cursor-pointer hover:bg-gray-100">Item</th>
                <th onClick={() => handleSort('category')} className="text-left p-3 font-semibold text-gray-600 cursor-pointer hover:bg-gray-100">Category</th>
                <th onClick={() => handleSort('dept')} className="text-left p-3 font-semibold text-gray-600 cursor-pointer hover:bg-gray-100">Department</th>
                <th onClick={() => handleSort('total_qty')} className="text-right p-3 font-semibold text-gray-600 cursor-pointer hover:bg-gray-100">Qty Sold</th>
                <th onClick={() => handleSort('total_sales')} className="text-right p-3 font-semibold text-gray-600 cursor-pointer hover:bg-gray-100">Sales</th>
              </tr>
            </thead>
            <tbody>
              {summaryRows.map((item, i) => (
                <tr key={i} className="border-b hover:bg-gray-50">
                  <td className="p-3 font-medium">{item.menu_item__name}</td>
                  <td className="p-3 text-gray-600">{item.category}</td>
                  <td className="p-3 text-gray-600">{item.dept}</td>
                  <td className="p-3 text-right font-semibold">{item.total_qty}</td>
                  <td className="p-3 text-right font-semibold text-green-700">{formatCurrency(item.total_sales)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    if (result?.dept_summary) {
      const summaryRows = [...result.dept_summary].sort((a, b) => {
        if (!sortKey) {
          return compareValues(b.total_sales, a.total_sales, 'asc');
        }
        return compareValues(a[sortKey], b[sortKey], sortDir);
      });

      return (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th onClick={() => handleSort('dept_name')} className="text-left p-3 font-semibold text-gray-600 cursor-pointer hover:bg-gray-100">Department</th>
                <th onClick={() => handleSort('item_count')} className="text-right p-3 font-semibold text-gray-600 cursor-pointer hover:bg-gray-100">Unique Items</th>
                <th onClick={() => handleSort('total_qty')} className="text-right p-3 font-semibold text-gray-600 cursor-pointer hover:bg-gray-100">Qty Sold</th>
                <th onClick={() => handleSort('total_sales')} className="text-right p-3 font-semibold text-gray-600 cursor-pointer hover:bg-gray-100">Sales</th>
              </tr>
            </thead>
            <tbody>
              {summaryRows.map((dept, i) => (
                <tr key={i} className="border-b hover:bg-gray-50">
                  <td className="p-3 font-medium">{dept.dept_name}</td>
                  <td className="p-3 text-right">{dept.item_count}</td>
                  <td className="p-3 text-right font-semibold">{dept.total_qty}</td>
                  <td className="p-3 text-right font-semibold text-green-700">{formatCurrency(dept.total_sales)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    return null;
  };

  const hasSummaryTab = result?.item_summary || result?.dept_summary;

  return (
    <div className={`fixed inset-0 ${zIndexClass} flex items-start justify-center bg-black/50 overflow-y-auto p-2 sm:p-4`}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl my-2 sm:my-8 max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-primary-50 to-white rounded-t-xl">
          <div>
            <h2 className="text-lg font-bold text-gray-800">{propTitle || result?.title || 'Drill Down'}</h2>
            <p className="text-xs text-gray-500 mt-1">{filteredData.length} records found</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="px-4 pt-4">
          {!loading && result && renderSummaryCards()}
        </div>

        {/* Filters Bar */}
        <div className="px-4 pb-2 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
            />
          </div>
          {departments.length > 1 && (
            <select
              value={filterDept}
              onChange={(e) => { setFilterDept(e.target.value); setFilterCategory(''); setPage(1); }}
              className="text-sm border rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-300"
            >
              <option value="">All Departments</option>
              {departments.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          )}
          {categories.length > 1 && (
            <select
              value={filterCategory}
              onChange={(e) => { setFilterCategory(e.target.value); setPage(1); }}
              className="text-sm border rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-300"
            >
              <option value="">All Categories</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          {(filterDept || filterCategory || search) && (
            <button
              onClick={() => { setFilterDept(''); setFilterCategory(''); setSearch(''); setPage(1); }}
              className="text-xs text-red-600 hover:text-red-800 px-2 py-1 border border-red-200 rounded-lg"
            >
              Clear Filters
            </button>
          )}
        </div>

        {/* Tabs */}
        {hasSummaryTab && (
          <div className="px-4 flex gap-1 border-b">
            <button
              onClick={() => setActiveTab('detail')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
                activeTab === 'detail' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Detail View
            </button>
            <button
              onClick={() => setActiveTab('summary')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
                activeTab === 'summary' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Summary
            </button>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-auto px-4 pb-4">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
            </div>
          ) : error ? (
            <div className="text-center py-20 text-red-600">{error}</div>
          ) : activeTab === 'summary' && hasSummaryTab ? (
            renderSummaryTab()
          ) : pagedData.length === 0 ? (
            <div className="text-center py-20 text-gray-400">No records found</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-white z-10">
                    <tr className="bg-gray-50 border-b">
                      {columns.map(col => (
                        <th
                          key={col}
                          onClick={() => handleSort(col)}
                          className="text-left p-2.5 font-semibold text-gray-600 cursor-pointer hover:bg-gray-100 whitespace-nowrap select-none"
                        >
                          <span className="flex items-center gap-1">
                            {columnLabels[col] || col.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                            {sortKey === col && (
                              <ArrowUpDown size={12} className={`text-primary-600 ${sortDir === 'desc' ? 'rotate-180' : ''}`} />
                            )}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pagedData.map((row, i) => {
                      const orderClickable = onOrderClick && row.order_number;
                      const rowClickable = !orderClickable && onRowClick;
                      const isClickable = orderClickable || rowClickable;
                      const isPrevSale = row.is_previous_sale;
                      return (
                        <tr
                          key={i}
                          className={`border-b transition ${isPrevSale ? 'bg-amber-50' : ''} ${isClickable ? 'cursor-pointer hover:bg-orange-50' : 'hover:bg-gray-50'}`}
                          onClick={orderClickable ? () => onOrderClick(row.order_number) : (rowClickable ? () => onRowClick(row) : undefined)}
                        >
                          {columns.map(col => (
                            <td key={col} className={`p-2.5 whitespace-nowrap ${
                              ['total_price','unit_price','cost_impact','amount','total','revenue','avg_price','cost','avg_per_order'].includes(col) ? 'text-right font-medium' :
                              col === 'quantity' || col === 'qty' || col === 'count' ? 'text-center font-semibold' : ''
                            }`}>
                              {renderCellValue(col, row[col])}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-3 border-t">
                  <p className="text-xs text-gray-500">
                    Showing {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, filteredData.length)} of {filteredData.length}
                  </p>
                  <div className="flex items-center gap-1">
                    <button
                      disabled={page <= 1}
                      onClick={() => setPage(p => p - 1)}
                      className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) pageNum = i + 1;
                      else if (page <= 3) pageNum = i + 1;
                      else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
                      else pageNum = page - 2 + i;
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setPage(pageNum)}
                          className={`w-8 h-8 rounded text-sm font-medium ${
                            page === pageNum ? 'bg-primary-600 text-white' : 'hover:bg-gray-100 text-gray-600'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    <button
                      disabled={page >= totalPages}
                      onClick={() => setPage(p => p + 1)}
                      className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardDrillDown;
